import { FC, useMemo, useState, useEffect } from 'react';
import { RadixTable, TColumn } from '@features/ui';
import { Consent, TermAcceptance } from '@src/types/invoices';
import { getUserConsent } from '@src/services/users';
import { formatDateTimeShort } from '@lib/utils';
import { toast } from 'react-toastify';

type Row = {
  id: number;
  title: string;
  type: string;
  obtained_at: string;
  ip: string;
  source: string;
  status: string;
  dataType: 'consent' | 'term';
};

interface UserConsentProps {
  userId: number;
}

export const UserConsent: FC<UserConsentProps> = ({ userId }) => {
  const [data, setData] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchConsents = async () => {
      if (!userId) return;

      setLoading(true);
      try {
        const response = await getUserConsent(userId);
        const { consents, term_acceptances } = response.data;

        // Map consents
        const consentRows: Row[] = consents.map((consent: Consent) => ({
          id: consent.id,
          title: consent.title || consent.am_agreement?.title || 'N/A',
          type: consent.type,
          obtained_at: consent.created_at,
          ip: consent.remote_address,
          source: consent.source,
          status: consent.cancel_dattm ? 'Cancelled' : 'Accepted',
          dataType: 'consent' as const,
        }));

        // Map term acceptances
        const termRows: Row[] = term_acceptances.map(
          (term: TermAcceptance) => ({
            id: term.id,
            title: term.content,
            type: term.type,
            obtained_at: term.accepted_at,
            ip: term.remote_addr,
            source: 'Terms Acceptance',
            status: 'Accepted',
            dataType: 'term' as const,
          })
        );

        // Combine và sort theo obtained_at (newest first)
        const combinedData = [...consentRows, ...termRows].sort(
          (a, b) =>
            new Date(b.obtained_at).getTime() -
            new Date(a.obtained_at).getTime()
        );

        setData(combinedData);
      } catch (error) {
        console.error('Error fetching consents:', error);
        toast.error('Failed to load consents');
      } finally {
        setLoading(false);
      }
    };

    fetchConsents();
  }, [userId]);

  const columns: TColumn<Row>[] = useMemo(
    () => [
      { title: 'DOCUMENT TITLE', render: (r) => <span>{r?.title ?? ''}</span> },
      { title: 'DOCUMENT TYPE', render: (r) => <span>{r?.type ?? ''}</span> },
      {
        title: 'OBTAINED AT',
        render: (r) => (
          <span>
            {r?.obtained_at ? formatDateTimeShort(r.obtained_at) : ''}
          </span>
        ),
      },
      { title: 'IP ADDRESS', render: (r) => <span>{r?.ip ?? ''}</span> },
      {
        title: 'SOURCE OF CONSENT',
        render: (r) => <span>{r?.source ?? ''}</span>,
      },
    ],
    []
  );

  if (loading) {
    return <div className="text-center py-8">Loading consents...</div>;
  }

  return <RadixTable columns={columns} data={data} />;
};
